import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Comment } from '@/core/models/comment';
import { Button, MetaRow, Popover } from '@/ui';
import { discussionHref } from '@/core/services/discussionsService';
import { useDiscussionTools } from './context';
import { Card, Quote, Row, Small, Stack } from './styles';

export function DiscussionToolbar({ postId }: { postId: string }) {
  const tools = useDiscussionTools();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);
  if (!tools) return null;
  const { state, open, ownPost, authenticated, run } = tools;
  const paused =
    state?.private?.snoozedForever ||
    !!(state?.private?.snoozedUntil && Date.parse(state.private.snoozedUntil) > now);
  return (
    <Stack>
      <Row>
        <Button size="sm" variant="ghost" onClick={() => open('search')}>
          Search discussion
        </Button>
        <Button size="sm" variant="ghost" onClick={() => open('sources')}>
          Sources
        </Button>
        {authenticated && (
          <>
            <Button size="sm" variant="ghost" onClick={() => open('snooze')}>
              {paused ? 'Notifications paused' : 'Pause notifications'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => open('bookmark')}>
              Save with private note
            </Button>
            <Button size="sm" variant="ghost" onClick={() => open('labels')}>
              Labels
            </Button>
            <Button size="sm" variant="ghost" onClick={() => open('invite')}>
              Invite
            </Button>
          </>
        )}
        {ownPost && (
          <>
            <Button size="sm" variant="ghost" onClick={() => open('update')}>
              Add an update
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={!state}
              onClick={() => void run({ action: 'question', enabled: !state?.question })}
            >
              {state?.question ? 'Remove question status' : 'Mark as a question'}
            </Button>
          </>
        )}
      </Row>
      {state?.updates.map((u) => (
        <Card key={u.id}>
          <strong>Author’s update</strong>
          <Small>{new Date(u.createdAt).toLocaleString()}</Small>
          <p>{u.body}</p>
          {ownPost && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void run({ action: 'removeUpdate', id: u.id })}
            >
              Remove update
            </Button>
          )}
        </Card>
      ))}
      {state?.question && (
        <Small>
          <MetaRow>
            <span>Question</span>
            <span>The author can choose multiple helpful answers.</span>
          </MetaRow>
        </Small>
      )}
      {!!state?.helpful.length && (
        <Card>
          <strong>Author’s picks ({state.helpful.length})</strong>
          <Small>Chosen by the post author. Comment sorting below is unchanged.</Small>
          {state.helpful.map((c) => (
            <Stack key={c.id}>
              <Link to={discussionHref(postId, c.id)}>
                {c.author}: {c.body.slice(0, 300)}
              </Link>
              {c.reason && <Small>Author’s reason: {c.reason}</Small>}
              {ownPost && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => void run({ action: 'helpful', commentId: c.id, remove: true })}
                >
                  Remove pick
                </Button>
              )}
            </Stack>
          ))}
        </Card>
      )}
    </Stack>
  );
}

export function CommentTools({ comment, isOwn }: { comment: Comment; isOwn: boolean }) {
  const tools = useDiscussionTools();
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  if (!tools || !tools.authenticated || comment.deleted) return null;
  const selected = tools.selected.some((c) => c.id === comment.id);
  const action = (tool: 'quote' | 'bookmark' | 'helpful' | 'reconsider') => {
    trigger.current?.focus();
    setOpen(false);
    tools.open(tool, comment);
  };
  return (
    <Row>
      <Popover
        open={open}
        onOpenChange={setOpen}
        trigger={
          <Button ref={trigger} size="sm" variant="ghost">
            More reply options
          </Button>
        }
      >
        <Stack>
          <Button variant="ghost" onClick={() => action('quote')}>
            Quote a passage
          </Button>
          <Button variant="ghost" onClick={() => action('bookmark')}>
            Save with private note
          </Button>
          <Button
            variant="ghost"
            disabled={!selected && tools.selected.length >= 3}
            onClick={() => {
              tools.select(comment);
              setOpen(false);
            }}
          >
            {selected ? 'Deselect comment' : 'Select for combined reply'}
          </Button>
          {tools.ownPost && tools.state?.question && (
            <Button variant="ghost" onClick={() => action('helpful')}>
              Choose as helpful
            </Button>
          )}
          {isOwn && (
            <Button variant="ghost" onClick={() => action('reconsider')}>
              I reconsidered this
            </Button>
          )}
        </Stack>
      </Popover>
      {tools.selected.length > 0 && (
        <Button size="sm" variant="secondary" onClick={() => tools.open('reply')}>
          Reply to {tools.selected.length} selected
        </Button>
      )}
    </Row>
  );
}

export function CommentReferences({ comment }: { comment: Comment }) {
  const tools = useDiscussionTools();
  const context = tools?.state?.contexts[comment.id];
  const followups = Object.entries(tools?.state?.contexts ?? {}).filter(
    ([, c]) => c.reconsidered?.id === comment.id,
  );
  if (!context && !followups.length) return null;
  return (
    <Stack>
      {context?.references.map((r, i) => (
        <Quote key={i}>
          {r.unavailable ? (
            <Small>Source comment is no longer available.</Small>
          ) : (
            <>
              <Link to={discussionHref(comment.postId, r.id)}>{r.handle}</Link>
              <p>{r.excerpt}</p>
              {r.edited && (
                <Small>
                  <MetaRow>
                    <span>Edited since quoted</span>
                    <Link to={discussionHref(comment.postId, r.id)}>
                      View original and edit history
                    </Link>
                  </MetaRow>
                </Small>
              )}
            </>
          )}
        </Quote>
      ))}
      {context?.reconsidered && (
        <Quote>
          <strong>Reconsidered</strong>
          <p>
            <Link to={discussionHref(comment.postId, context.reconsidered.id)}>
              Read my earlier comment
            </Link>
          </p>
        </Quote>
      )}
      {followups.map(([id]) => (
        <Small key={id}>
          The author added a reflection:{' '}
          <Link to={discussionHref(comment.postId, id)}>Read follow-up</Link>
        </Small>
      ))}
    </Stack>
  );
}

export function SelectableCommentBody({
  comment,
  children,
}: {
  comment: Comment;
  children: React.ReactNode;
}) {
  const tools = useDiscussionTools();
  const [selection, setSelection] = useState('');
  return (
    <div
      onMouseUp={(e) => {
        const s = window.getSelection();
        const value = s?.toString().trim() ?? '';
        setSelection(
          s &&
            e.currentTarget.contains(s.anchorNode) &&
            e.currentTarget.contains(s.focusNode) &&
            value.length <= 600 &&
            comment.body.includes(value)
            ? value
            : '',
        );
      }}
    >
      {children}
      {!!selection && tools?.authenticated && (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            tools.open('quote', comment, selection);
            setSelection('');
          }}
        >
          Reply with selected quote
        </Button>
      )}
    </div>
  );
}
